/**
 * Manifesto estruturado do portfólio para correlação semântica (Gemini)
 * e enriquecimento client-side dos IDs retornados no tabuleiro.
 *
 * IDs estáveis: `{dieType}:{face}` — ex.: d12:1, d4:3
 */
import {
  DICE_CATEGORIES,
  DICE_ORDER,
  EXPERTISE_DATA,
} from './expertise.js';

/**
 * @typedef {{ id: string, die: string, face: number, category: string, categoryShort: string, title: string, text: string, kind: 'quest'|'attribute'|'principle'|'ecosystem'|'experience'|'foundation' }} ManifestItem
 */

const KIND_BY_DIE = {
  d4: 'attribute',
  d6: 'experience',
  d8: 'foundation',
  d10: 'principle',
  d12: 'quest',
  d20: 'principle',
  d100: 'ecosystem',
};

/** @returns {ManifestItem[]} */
export function buildPortfolioManifest() {
  /** @type {ManifestItem[]} */
  const items = [];

  for (const die of DICE_ORDER) {
    const cat = DICE_CATEGORIES[die];
    const group = EXPERTISE_DATA[die];
    if (!cat || !group) continue;

    for (const [faceKey, entry] of Object.entries(group.entries)) {
      if (!entry || entry.placeholder) continue;
      const face = Number(faceKey);
      items.push({
        id: `${die}:${face}`,
        die,
        face,
        category: cat.name,
        categoryShort: cat.short,
        title: entry.title,
        text: entry.text,
        kind: KIND_BY_DIE[die] ?? 'principle',
      });
    }
  }

  return items;
}

/** Mapa id → item para lookup O(1) no tabuleiro. */
export function buildManifestIndex() {
  return new Map(buildPortfolioManifest().map((item) => [item.id, item]));
}

/**
 * Versão compacta para system prompt / structured context (economia de tokens).
 * @returns {string}
 */
export function manifestForPrompt() {
  const items = buildPortfolioManifest();
  return items
    .map(
      (i) =>
        `[${i.id}] (${i.kind}/${i.categoryShort}) ${i.title} — ${i.text}`,
    )
    .join('\n');
}

export const PORTFOLIO_MANIFEST = buildPortfolioManifest();
